import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { TranslationUploadResult } from '@/connection/translations/translations';

interface PageTranslationsUploadModalProps {
  open: boolean;
  onClose: () => void;
  result: TranslationUploadResult | null;
  onApply: () => void;
  onReupload: () => void;
  applying: boolean;
}

const PageTranslationsUploadModal = ({
  open,
  onClose,
  result,
  onApply,
  onReupload,
  applying,
}: PageTranslationsUploadModalProps) => {
  const warnings = result?.warnings ?? [];
  const hasWarnings = warnings.length > 0;

  return (
    <Modal
      backgroundColor="var(--global-cont-1)"
      open={open}
      onRequestClose={onClose}
      customSize={{
        width: '640px',
        maxWidth: 'calc(100% - 32px)',
        height: 'auto',
        maxHeight: '680px',
      }}
      RenderFooterContent={() => (
        <Container
          className="flex ai-center jc-space-between"
          padding="24px 40px"
          background="var(--global-cont-1)"
          borderRadius="0 0 12px 12px"
        >
          <Button design="LocalBttnSec" onClick={onReupload} disabled={applying} height="40px">
            <Text className="L1">Reupload</Text>
          </Button>
          <Container className="flex gap-16 ai-center">
            <Button design="LocalBttnSec" onClick={onClose} disabled={applying} height="40px">
              <Text className="L1">Cancel</Text>
            </Button>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              height="40px"
              minWidth="152px"
              loading={applying}
              disabled={applying}
              onClick={onApply}
              hoverOpacity={0.9}
              className="flex ai-center jc-center"
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Apply translations
              </Text>
            </Button>
          </Container>
        </Container>
      )}
    >
      <Container background="var(--global-cont-1)" padding="24px 40px 16px 40px" borderRadius="12px 12px 0 0">
        <Text tag="h4" className="H4" design={hasWarnings ? 'GlobalError400' : 'GlobalIcoText1'}>
          {hasWarnings ? 'Some fields were not translated' : 'Upload complete'}
        </Text>
        {hasWarnings ? (
          <>
            <Text className="P1 margin-t-4" design="GlobalIcoText3">
              {warnings.length} field{warnings.length !== 1 ? 's' : ''} missing a translation
            </Text>
            <Container className="flex-column gap-10 overflow-y-scroll no-scrollbar margin-t-16" maxHeight="400px">
              {warnings.map((warning, i) => (
                <Text key={i} className="P1" design="GlobalIcoText2">
                  {warning.value}
                </Text>
              ))}
            </Container>
          </>
        ) : (
          <Text className="P2 margin-t-8" design="GlobalIcoText2">
            All fields were successfully translated.
          </Text>
        )}
      </Container>
    </Modal>
  );
};

export default PageTranslationsUploadModal;
